import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';

import Lottie from 'react-lottie';
import parsePhoneNumber from 'libphonenumber-js'
import { Map } from 'immutable';

import { useTheme } from '@mui/material/styles';
import { alpha } from "@mui/material";
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import FileUpload from 'components/Generic/Inputs/FileUpload';
import ModalBox from 'components/Generic/MUI/ModalBox';
import Display from 'components/Generic/Display';
import directUpload from 'helpers/direct_upload';
import { CANDIDATE_APPLICATION_FREE_FORM_APPLY } from 'queries/candidate/apply';

import emptyAnimation from 'assets/landing_page/file_upload/empty.json';
import errorAnimation from 'assets/landing_page/file_upload/error.json';
import processingAnimation from 'assets/landing_page/file_upload/processing.json';
import successAnimation from 'assets/landing_page/file_upload/success.json';

import { STATUSES, ACCEPTED_FILE_TYPES, ANIMATION_DEFAULT_OPTIONS, TRY_AGAIN_STATUSES } from './constants';
import Progress from './Progress';

const GIF_ANIMATIONS = {
  [STATUSES.EMPTY]: emptyAnimation,
  [STATUSES.UPLOADING]: processingAnimation,
  [STATUSES.PARSING]: processingAnimation,
  [STATUSES.FAILED]: errorAnimation,
  [STATUSES.MISSING_CONTACT_DETAILS]: errorAnimation,
  [STATUSES.NOT_SUPPORTED_FILE]: errorAnimation,
  [STATUSES.INVALID_FILE]: errorAnimation,
  [STATUSES.SUCCESS]: successAnimation,
}

function Index({ onProcessed, campaignId }) {
  const theme = useTheme();

  const [state, setState] = useState(new Map());

  const modalOpen = state.get('modalOpen', false);
  const file = state.get('file', null);
  const status = state.get('status', STATUSES.EMPTY);
  const signedBlobId = state.get('signedBlobId', null);
  const extractedText = state.get('extractedText', null);

  const [toUpload, setToUpload] = useState(null);

  const [uploadResponse, uploadError, uploadFinished, uploadProgress] = directUpload(toUpload, 'candidate_resume', 'Resume', null);
  const [buildApplication, { loading, error, data }] = useMutation(CANDIDATE_APPLICATION_FREE_FORM_APPLY);

  const toggleModal = () => {
    setState(state.set('modalOpen', !modalOpen));
  };

  const onRemoveFile = () => {
    setState(state.set('status', STATUSES.EMPTY).set('file', null).set('signedBlobId', null));
    setToUpload(null);
  };

  const onFileChange = (_, value) => {
    if (!value) return

    if (value.get('blobs')[0].errors) {
      setState(state.set('status', STATUSES.NOT_SUPPORTED_FILE).set('file', value.get('blobs')[0]).set('extractedText', null));
      return;
    }

    setState(state.set('status', STATUSES.UPLOADING).set('file', value.get('blobs')[0].blob).set('extractedText', null));
    setToUpload(value.get('blobs')[0].blob);
  }

  useEffect(() => {
    if (!uploadFinished) return;

    if (uploadError?.upload || uploadError?.textExtraction) {
      setState(state => state.set('status', STATUSES.FAILED));
      return;
    }

    setState(state => state.set('status', STATUSES.PARSING).set('signedBlobId', uploadResponse.signedBlobId).set('extractedText', uploadResponse.textExtraction.result));
  }, [uploadResponse, uploadError, uploadFinished, uploadProgress]);

  useEffect(() => {
    if (!extractedText) return;

    buildApplication({ variables: { applications: [{ campaignId, rawData: extractedText }] }});
  }, [extractedText]);

  useEffect(() => {
    if (!data) return;

    const response = (data?.candidate?.applications?.applications || [{}])[0];

    const profileAttributes = (response.profileAttributes || []).map(
      (item) => ({ key: item.key.replace(/^others\./, ""), value: item.value })
    );

    const insights =  { answerSuggestions: response.insights?.answerSuggestions, rawData: extractedText, dataSource: 'resume' };
    const phone = parsePhoneNumber(response.msisdn ? `+${response.msisdn}` : '');
    const msisdn = phone && phone.isValid() ? response.msisdn : '';

    const object = {
      firstName: response.firstName,
      lastName: response.lastName,
      email: response.email,
      education: response.education,
      signedBlobId,
      profileAttributes,
      insights,
      msisdn,
    };

    if (!object.firstName || !object.lastName || (!object.email && !object.msisdn)) {
      setState(state => state.set('status', STATUSES.MISSING_CONTACT_DETAILS));
      return;
    }

    setState(state => state.set('status', STATUSES.SUCCESS).set('modalOpen', false));
    onProcessed(object);
  }, [data]);

  useEffect(() => {
    if (!error) return;

    setState(state => state.set('status', STATUSES.FAILED));
  }, [error]);

  const fileContainerColor = useMemo(() => {
    return TRY_AGAIN_STATUSES.includes(status) ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.primary.main, 0.1);
  }, [status]);

  const fileUploadButtonLabel = useMemo(() => {
    if (status === STATUSES.EMPTY) return t("eos.landing_page.apply_resume.modal.button.choose_file");

    return t("eos.landing_page.apply_resume.modal.button.upload_again");
  }, [status]);

  const fileUploadButtonVariant = useMemo(() => {
    if (status === STATUSES.EMPTY) return 'contained';

    return 'outlined';
  }, [status]);

  return (
    <>
      <Button
        onClick={toggleModal}
        startIcon={<AutoAwesomeIcon sx={{ color: theme.palette.ai.dark }}/>}
        variant="contained"
        disableElevation={true}
        sx={{
          backgroundColor: theme.palette.ai.main,
          '&:hover': {
            backgroundColor: theme.palette.ai.main,
          },
          color: theme.palette.ai.contrastText,
        }}
      >
        {t('eos.landing_page.apply_resume.title')}
      </Button>

      <Modal
        open={modalOpen}
        onClose={toggleModal}
      >
        <ModalBox sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
          <Box sx={{ alignItems: 'center', height: '15vh', width: '15vh' }}>
            <Lottie options={{ ...ANIMATION_DEFAULT_OPTIONS, animationData: GIF_ANIMATIONS[status] }} />
          </Box>

          <Typography variant="h4" sx={{ pb: 2 }}>{t(`eos.landing_page.apply_resume.modal.title.${status}`)}</Typography>

          <Display if={status !== STATUSES.SUCCESS}>
            <Typography variant="body2" sx={{ pb: 2 }}>{t(`eos.landing_page.apply_resume.modal.description.${status}`)}</Typography>
          </Display>

          <Display if={file}>
            <Grid container columns={16} spacing={0} data-testid="file" sx={{ width: '100%', p: 2, backgroundColor: fileContainerColor }}>
              <Grid item sm={status === STATUSES.SUCCESS ? 16 : 8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  data-testid="file-upload-file-name"
                  variant="p"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap"
                  }}
                >
                  {file?.name}
                </Typography>
              </Grid>

              <Display if={[STATUSES.UPLOADING, STATUSES.PARSING].includes(status)}>
                <Grid item sm={7} sx={{ display: 'flex', alignItems: 'center', pl: 2 }} data-testid="fileupload-file-progress">
                  <Progress progress={uploadProgress} loading={loading} />
                </Grid>

                <Grid item sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <CloseIcon data-testid="fileupload-file-remove" sx={{ alignSelf: 'end', display: 'flex', direction: 'row-reverse', cursor: 'pointer' }} onClick={onRemoveFile} color="primary" />
                </Grid>
              </Display>

              <Display if={TRY_AGAIN_STATUSES.includes(status)}>
                <Grid item sm={8} sx={{ display: 'flex',  alignItems: 'center', flexDirection: 'row-reverse' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <ErrorIcon />
                    <Typography variant="p" component="p" noWrap sx={{ pl: 1 }}>{t(`eos.landing_page.text.upload.status.upload_error`)}</Typography>
                  </Box>
                </Grid>
              </Display>
            </Grid>
          </Display>

          <Display if={status === STATUSES.EMPTY || TRY_AGAIN_STATUSES.includes(status)}>
            <FileUpload
              name="resume"
              multiple={false}
              onChange={onFileChange}
              variant={fileUploadButtonVariant}
              label={fileUploadButtonLabel}
              types={ACCEPTED_FILE_TYPES}
            />
          </Display>
        </ModalBox>
      </Modal>
    </>
  );
}

Index.propTypes = {
  onProcessed: PropTypes.func,
  campaignId:  PropTypes.string,
};

export default Index;
